/**
 * Gemeinsame Seitenlogik: Anfragezaehler, Vollbild-Menue, Kopfverhalten,
 * sanftes Scrollen und Theme-Umschalter.
 *
 * Alles hier ist Komfort. Ohne JavaScript bleibt jede Seite bedienbar:
 * die Hauptnavigation steht im Kopf, das Menue ist dann schlicht nicht da.
 */

import Lenis from "lenis";
import { anzahl } from "./warenkorb.ts";
import { hatText, t, zahl } from "./i18n.ts";

const bewegungReduziert = (): boolean => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Beschriftung des Kopf-Links, wie der Server sie in der Sprache der Seite liefert. */
let listeBasisText: string | null = null;

function aktualisiereZaehler(): void {
  const ziel = document.querySelector<HTMLAnchorElement>(".kopf-liste");
  if (!ziel) return;
  listeBasisText ??= ziel.textContent ?? "";
  const n = anzahl();
  ziel.textContent = n > 0 ? `${listeBasisText} (${zahl(n)})` : listeBasisText;
  // Die Zusatzbeschreibung gibt es nur, wenn die Seite den Namensraum "site" eingebettet hat.
  if (!hatText("site.liste.aria_leer")) return;
  ziel.setAttribute("aria-label", n > 0 ? t("site.liste.aria", { n }) : t("site.liste.aria_leer"));
}

let lenis: Lenis | null = null;

function sanftesScrollen(): void {
  if (bewegungReduziert()) return;
  lenis = new Lenis({ duration: 1.1, smoothWheel: true, anchors: true });
  const schritt = (zeit: number): void => {
    lenis?.raf(zeit);
    requestAnimationFrame(schritt);
  };
  requestAnimationFrame(schritt);
}

function menue(): void {
  const knopf = document.querySelector<HTMLButtonElement>(".menue-knopf");
  const panel = document.getElementById("menue");
  if (!knopf || !panel) return;
  panel.hidden = false;

  const setze = (offen: boolean): void => {
    knopf.setAttribute("aria-expanded", String(offen));
    panel.classList.toggle("ist-offen", offen);
    document.body.classList.toggle("menue-offen", offen);
    if (offen) {
      lenis?.stop();
      panel.querySelector<HTMLAnchorElement>("a")?.focus({ preventScroll: true });
    } else {
      lenis?.start();
    }
  };
  knopf.addEventListener("click", () => setze(knopf.getAttribute("aria-expanded") !== "true"));
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && panel.classList.contains("ist-offen")) {
      setze(false);
      knopf.focus();
    }
  });
}

/**
 * Kopf: blendet beim Runterscrollen aus, beim Hochscrollen ein, bekommt nach
 * dem ersten Bildschirm einen Glasgrund und uebernimmt den Ton (hell/dunkel)
 * der Sektion, ueber der er gerade steht.
 */
function kopfVerhalten(): void {
  const kopf = document.querySelector<HTMLElement>("[data-kopf]");
  if (!kopf) return;
  const startTon = document.body.dataset.kopfTon ?? "hell";
  let letztesY = window.scrollY;

  const tonBestimmen = (): void => {
    const y = kopf.getBoundingClientRect().height / 2;
    let ton = startTon;
    for (const s of Array.from(document.querySelectorAll<HTMLElement>("[data-kopf-abschnitt]"))) {
      const r = s.getBoundingClientRect();
      if (r.top <= y && r.bottom >= y) ton = s.dataset.kopfAbschnitt ?? ton;
    }
    document.body.dataset.kopfTon = ton;
  };

  const beimScrollen = (): void => {
    const y = window.scrollY;
    kopf.classList.toggle("ist-gescrollt", y > 40);
    const runter = y > letztesY && y > 240;
    if (!document.body.classList.contains("menue-offen")) kopf.classList.toggle("ist-versteckt", runter);
    letztesY = y;
    tonBestimmen();
  };
  window.addEventListener("scroll", beimScrollen, { passive: true });
  beimScrollen();
}

function themeUmschalter(): void {
  const ziel = document.querySelector(".menue-fuss");
  if (!ziel) return;
  const b = document.createElement("button");
  b.type = "button";
  let gespeichert: string | null = null;
  try {
    gespeichert = localStorage.getItem("alumundo.theme");
  } catch {
    gespeichert = null;
  }
  if (gespeichert === "dark" || gespeichert === "light") {
    document.documentElement.setAttribute("data-theme", gespeichert);
  }
  const istDunkel = (): boolean =>
    document.documentElement.getAttribute("data-theme") === "dark" ||
    (!document.documentElement.getAttribute("data-theme") &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);
  if (!hatText("site.theme.hell")) return;
  const setzeText = (): void => {
    b.textContent = istDunkel() ? t("site.theme.hell") : t("site.theme.dunkel");
  };
  setzeText();
  b.addEventListener("click", () => {
    const neu = istDunkel() ? "light" : "dark";
    document.documentElement.setAttribute("data-theme", neu);
    try {
      localStorage.setItem("alumundo.theme", neu);
    } catch {
      /* Speicher gesperrt: Umschalten gilt dann nur fuer diese Seite */
    }
    setzeText();
  });
  ziel.append(b);
}

document.addEventListener("DOMContentLoaded", () => {
  aktualisiereZaehler();
  sanftesScrollen();
  menue();
  kopfVerhalten();
  themeUmschalter();
});
document.addEventListener("alumundo:liste-geaendert", aktualisiereZaehler);
